import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Eye, User, ArrowRight, FileText, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getPublishedNews, Article, getImageUrl, formatDate, formatReadingTime } from '@/lib/api';

const ArticlesSection = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await getPublishedNews({
          limit: 6, // Show latest 6 articles
          sort: 'latest'
        });

        // The getPublishedNews function already extracts data.data, so response should be an array
        if (Array.isArray(response)) {
          setArticles(response);
        } else {
          console.error('Unexpected response format:', response);
          setArticles([]);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-white via-primary/5 to-white relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-10 left-[5%] w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-10 right-[5%] w-72 h-72 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">
              Berita & <span className="text-transparent bg-clip-text bg-gradient-hero">Artikel Terkini</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Informasi dan insight terpercaya untuk mendukung kesehatan mentalmu setiap hari.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="glass-card p-5 rounded-2xl border border-border/40 animate-pulse flex flex-col h-[400px]">
                <div className="aspect-video w-full bg-muted rounded-xl mb-4" />
                <div className="h-4 bg-muted rounded w-1/4 mb-3" />
                <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                <div className="h-12 bg-muted rounded w-full mb-6" />
                <div className="mt-auto flex justify-between items-center">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-4 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return (
      <section className="py-20 bg-gradient-to-b from-white via-primary/5 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">
              Berita & <span className="text-transparent bg-clip-text bg-gradient-hero">Artikel Terkini</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Dapatkan informasi terbaru seputar kesehatan mental dan kesejahteraan dari tim ahli kami
            </p>
          </div>
          <div className="text-center py-16 bg-white/40 backdrop-blur-md rounded-3xl border border-border/40 max-w-lg mx-auto shadow-sm">
            <FileText className="mx-auto h-16 w-16 text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">
              Belum ada artikel tersedia
            </h3>
            <p className="text-muted-foreground">
              Silakan kembali lagi nanti untuk artikel terbaru dari kami.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white via-primary/5 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 left-[10%] w-80 h-80 bg-primary/5 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-20 right-[10%] w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 text-primary rounded-full px-6 py-2 mb-4 text-xs font-bold uppercase tracking-wider shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span>Edukasi & Insight</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4">
            Berita & <span className="text-transparent bg-clip-text bg-gradient-hero">Artikel Terkini</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base">
            Dapatkan informasi terbaru seputar kesehatan mental dan kesejahteraan langsung dari tim ahli kami
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {articles.map((article) => (
            <Card key={article.id} className="glass-card hover:glass-card-hover border-border/40 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2.5 transition-all duration-500 group rounded-2xl flex flex-col h-full">
              {/* Featured Image */}
              <div className="aspect-video overflow-hidden bg-muted relative">
                {article.featured_image ? (
                  <img
                    src={getImageUrl(article.featured_image)}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="h-12 w-12 text-muted-foreground/40" />
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/95 backdrop-blur-sm text-primary border border-primary/10 text-xs font-bold py-1 px-3 shadow-md hover:bg-white transition-colors">
                    {article.category?.name || 'Artikel'}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-3 flex-grow p-6">
                <div className="flex items-center text-xs text-muted-foreground mb-3">
                  <Calendar className="h-3.5 w-3.5 mr-1.5 text-primary/60" />
                  {formatDate(article.published_at || article.created_at)}
                </div>
                <CardTitle className="line-clamp-2 text-lg font-bold group-hover:text-primary transition-colors leading-snug">
                  <Link to={`/news/${article.slug}`}>
                    {article.title}
                  </Link>
                </CardTitle>
                <CardDescription className="line-clamp-2 text-sm text-muted-foreground mt-2.5 leading-relaxed">
                  {article.excerpt}
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0 border-t border-border/10 mt-auto bg-white/30 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <div className="flex items-center gap-1.5 font-medium text-foreground/80">
                    <User className="h-3.5 w-3.5 text-primary" />
                    <span className="truncate max-w-[120px]">
                      {article.author?.name || 'Unknown Author'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-secondary" />
                      <span>{formatReadingTime(article.read_time_minutes)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5 text-accent" />
                      <span>{article.view_count}</span>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {article.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag.id} variant="outline" className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 border-border/60 text-muted-foreground bg-white/40">
                        #{tag.name}
                      </Badge>
                    ))}
                    {article.tags.length > 2 && (
                      <Badge variant="outline" className="text-[10px] font-semibold px-2 py-0.5 bg-white/40 border-border/60 text-muted-foreground">
                        +{article.tags.length - 2}
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Button asChild size="lg" className="gradient-primary group shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300 rounded-xl px-8 py-6">
            <Link to="/news" className="flex items-center text-base font-semibold">
              Lihat Lebih Banyak Artikel
              <ArrowRight className="ml-2.5 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground mt-4 font-medium">
            Temukan {articles.length > 6 ? 'lebih banyak' : 'semua'} artikel menarik tentang kesehatan mental
          </p>
        </div>
      </div>
    </section>
  );
};

export default ArticlesSection;